import { AusState } from "@aus/Store/AusState";
import DealerAccountRequestFormState from "@aus/Store/Components/DealerAccountRequestForm/DealerAccountRequestFormState";
import setFieldValue from "@aus/Store/Components/DealerAccountRequestForm/Handlers/SetFieldValue";
import submitDealerAccountRequestForm from "@aus/Store/Components/DealerAccountRequestForm/Handlers/SubmitDealerAccountRequestForm";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SettingsCollectionModel, SystemSettingsModel } from "@insite/client-framework/Services/SettingsService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { ListFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal from "@insite/mobius/Modal";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface DealerAccountRequestFormStyles {
    firstNameGridItem: GridItemProps;
    lastNameGridItem: GridItemProps;
    businessNameGridItem: GridItemProps;
    addressLine1GridItem: GridItemProps;
    addressLine2GridItem: GridItemProps;
    cityGridItem: GridItemProps;
    stateProvinceGridItem: GridItemProps;
    zipPostcodeGridItem: GridItemProps;
    countryGridItem: GridItemProps;
    phoneNumberGridItem: GridItemProps;
    faxNumberGridItem: GridItemProps;
    companyEmailGridItem: GridItemProps;
    companyWebsiteGridItem: GridItemProps;
    businessTypeGridItem: GridItemProps;
    businessDescriptionGridItem: GridItemProps;
    vatNumberGridItem: GridItemProps;
    nzCustomsNumberGridItem: GridItemProps;
    submitButtonGridItem: GridItemProps;
    errorMessage?: TypographyProps;
    submitButtonWrapper?: InjectableCss;
}

const styles: DealerAccountRequestFormStyles = {
    firstNameGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    lastNameGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    businessNameGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    addressLine1GridItem: {
        width: [12, 12, 12, 12, 12],
    },
    addressLine2GridItem: {
        width: [12, 12, 12, 12, 12],
    },
    cityGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    stateProvinceGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    zipPostcodeGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    countryGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    phoneNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    faxNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    companyEmailGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    companyWebsiteGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    businessTypeGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    businessDescriptionGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    vatNumberGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    nzCustomsNumberGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    submitButtonGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    errorMessage: {
        color: "danger",
        weight: 600,
    },
    submitButtonWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
};

export const formStyles = styles;

const enum fields {
    businessTypeOptions = "businessTypeOptions",
    successMessageTitle = "successMessageTitle",
    successMessage = "successMessage",
    errorMessageTitle = "errorMessageTitle",
    errorMessage = "errorMessage",
}

interface DropdownOption {
    fields: {
        value: string;
    };
}

interface OwnProps extends WidgetProps {
    dealerAccountRequestFormState: DealerAccountRequestFormState;
    fields: {
        [fields.businessTypeOptions]: DropdownOption[];
        [fields.successMessageTitle]: string;
        [fields.successMessage]: string;
        [fields.errorMessageTitle]: string;
        [fields.errorMessage]: string;
    };
}

const mapStateToProps = (state: AusState) => {
    const settingsCollection: SettingsCollectionModel = getSettingsCollection(state);
    const systemSettings = settingsCollection?.systemSettings;
    return {
        firstName: state.dealerAccountRequestForm?.fieldValues["firstName"],
        lastName: state.dealerAccountRequestForm?.fieldValues["lastName"],
        businessName: state.dealerAccountRequestForm?.fieldValues["businessName"],
        addressLine1: state.dealerAccountRequestForm?.fieldValues["addressLine1"],
        addressLine2: state.dealerAccountRequestForm?.fieldValues["addressLine2"],
        city: state.dealerAccountRequestForm?.fieldValues["city"],
        stateProvince: state.dealerAccountRequestForm?.fieldValues["stateProvince"],
        zipPostcode: state.dealerAccountRequestForm?.fieldValues["zipPostcode"],
        country: state.dealerAccountRequestForm?.fieldValues["country"],
        phoneNumber: state.dealerAccountRequestForm?.fieldValues["phoneNumber"],
        faxNumber: state.dealerAccountRequestForm?.fieldValues["faxNumber"],
        companyEmail: state.dealerAccountRequestForm?.fieldValues["companyEmail"],
        companyWebsite: state.dealerAccountRequestForm?.fieldValues["companyWebsite"],
        businessType: state.dealerAccountRequestForm?.fieldValues["businessType"],
        businessDescription: state.dealerAccountRequestForm?.fieldValues["businessDescription"],
        vatNumber: state.dealerAccountRequestForm?.fieldValues["vatNumber"],
        nzCustomsNumber: state.dealerAccountRequestForm?.fieldValues["nzCustomsNumber"],

        allCountries: getCurrentCountries(state),
        ausCountries: systemSettings?.ausCountries,
    };
};

const mapDispatchToProps = {
    setFieldValue,
    submitDealerAccountRequestForm,
    loadCurrentCountries,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const DealerAccoutnRequestForm: FC<Props> = ({
    firstName,
    lastName,
    businessName,
    addressLine1,
    addressLine2,
    city,
    stateProvince,
    zipPostcode,
    country,
    phoneNumber,
    faxNumber,
    companyEmail,
    companyWebsite,
    businessType,
    businessDescription,
    vatNumber,
    nzCustomsNumber,
    setFieldValue,
    submitDealerAccountRequestForm,
    loadCurrentCountries,
    fields,
    history,
    allCountries,
    ausCountries,
}) => {
    const [successModalIsOpen, setSuccessModalIsOpen] = React.useState(false);
    const [errorModalIsOpen, setErrorModalIsOpen] = React.useState(false);
    const [formErrorMessage, setFormErrorMessage] = React.useState("");
    const [firstNameError, setFirstNameError] = React.useState("");
    const [lastNameError, setLastNameError] = React.useState("");
    const [businessNameError, setBusinessNameError] = React.useState("");
    const [addressLine1Error, setAddressLine1Error] = React.useState("");
    const [cityError, setCityError] = React.useState("");
    const [stateProvinceError, setStateProvinceError] = React.useState("");
    const [zipPostcodeError, setZipPostcodeError] = React.useState("");
    const [countryError, setCountryError] = React.useState("");
    const [phoneNumberError, setPhoneNumberError] = React.useState("");
    const [companyEmailError, setCompanyEmailError] = React.useState("");
    const [vatNumberError, setVatNumberError] = React.useState("");
    const [vatNumberIsRequired, setVatNumberIsRequired] = React.useState(false);

    React.useEffect(() => {
        validateField("vatNumber", vatNumber);
    }, [vatNumberIsRequired]);

    React.useEffect(() => {
        loadCurrentCountries();
    }, []);

    const permanentRequiredFields = [
        "firstName",
        "lastName",
        "businessName",
        "addressLine1",
        "city",
        "stateProvince",
        "zipPostcode",
        "country",
        "phoneNumber",
        "companyEmail",
    ];

    const setInputError = (name: string, errorMessage: string) => {
        if (name === "firstName") {
            setFirstNameError(errorMessage);
        } else if (name === "lastName") {
            setLastNameError(errorMessage);
        } else if (name === "businessName") {
            setBusinessNameError(errorMessage);
        } else if (name === "addressLine1") {
            setAddressLine1Error(errorMessage);
        } else if (name === "city") {
            setCityError(errorMessage);
        } else if (name === "stateProvince") {
            setStateProvinceError(errorMessage);
        } else if (name === "zipPostcode") {
            setZipPostcodeError(errorMessage);
        } else if (name === "country") {
            setCountryError(errorMessage);
        } else if (name === "phoneNumber") {
            setPhoneNumberError(errorMessage);
        } else if (name === "companyEmail") {
            setCompanyEmailError(errorMessage);
        } else if (name === "vatNumber") {
            setVatNumberError(errorMessage);
        }
    };

    const validateField = (name: string, value?: string) => {
        const requiredFields = permanentRequiredFields;
        if (vatNumberIsRequired) {
            requiredFields.push("vatNumber");
        }
        if (requiredFields.includes(name) && !value) {
            setInputError(name, translate("Value is required"));
            return false;
        }

        setInputError(name, "");
        return true;
    };

    const getVatNumberIsRequired = (countryValue?: string) => {
        if (countryValue && ausCountries?.includes(countryValue)) {
            return true;
        }
        return false;
    };

    const validateForm = () => {
        let valid = validateField("firstName", firstName);
        valid = validateField("lastName", lastName) && valid;
        valid = validateField("businessName", businessName) && valid;
        valid = validateField("addressLine1", addressLine1) && valid;
        valid = validateField("city", city) && valid;
        valid = validateField("stateProvince", stateProvince) && valid;
        valid = validateField("zipPostcode", zipPostcode) && valid;
        valid = validateField("country", country) && valid;
        valid = validateField("phoneNumber", phoneNumber) && valid;
        valid = validateField("companyEmail", companyEmail) && valid;
        valid = validateField("vatNumber", vatNumber) && valid;
        return valid;
    };

    const submitForm = () => {
        if (!validateForm()) {
            setFormErrorMessage(translate("Resolve the above values before submitting the form"));
            return;
        }
        setFormErrorMessage("");
        submitDealerAccountRequestForm({
            onSuccess: () => {
                setSuccessModalIsOpen(true);
            },
            onError: () => {
                setErrorModalIsOpen(true);
            },
        });
    };

    const closeSuccessModal = () => {
        setSuccessModalIsOpen(false);
        history.push("/");
    };

    const closeErrorModal = () => {
        setErrorModalIsOpen(false);
    };

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({
            key: event.target.name,
            value: event.target.value,
        });
        validateField(event.target.name, event.target.value);
    };

    const onSelectCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        onSelectChange(event);
        setVatNumberIsRequired(getVatNumberIsRequired(event.target.value));
    };

    const onTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFieldValue({
            key: event.target.name,
            value: event.target.value,
        });
    };

    const onSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({
            key: event.target.name,
            value: event.target.value,
        });
    };

    return (
        <GridContainer>
            <GridItem {...styles.firstNameGridItem}>
                <TextField
                    label={translate("First Name")}
                    value={firstName || ""}
                    name="firstName"
                    required={true}
                    onChange={onInputChange}
                    error={firstNameError}
                ></TextField>
            </GridItem>
            <GridItem {...styles.lastNameGridItem}>
                <TextField
                    label={translate("Last Name")}
                    value={lastName || ""}
                    name="lastName"
                    required={true}
                    onChange={onInputChange}
                    error={lastNameError}
                ></TextField>
            </GridItem>
            <GridItem {...styles.businessNameGridItem}>
                <TextField
                    label={translate("Business Name")}
                    value={businessName || ""}
                    name="businessName"
                    required={true}
                    onChange={onInputChange}
                    error={businessNameError}
                ></TextField>
            </GridItem>
            <GridItem {...styles.addressLine1GridItem}>
                <TextField
                    label={translate("Address Line 1")}
                    value={addressLine1 || ""}
                    name="addressLine1"
                    required={true}
                    onChange={onInputChange}
                    error={addressLine1Error}
                ></TextField>
            </GridItem>
            <GridItem {...styles.addressLine2GridItem}>
                <TextField
                    label={translate("Address Line 2")}
                    value={addressLine2 || ""}
                    name="addressLine2"
                    onChange={onInputChange}
                ></TextField>
            </GridItem>
            <GridItem {...styles.cityGridItem}>
                <TextField
                    label={translate("City")}
                    value={city || ""}
                    name="city"
                    required={true}
                    onChange={onInputChange}
                    error={cityError}
                ></TextField>
            </GridItem>
            <GridItem {...styles.stateProvinceGridItem}>
                <TextField
                    label={translate("State/Province")}
                    value={stateProvince || ""}
                    name="stateProvince"
                    required={true}
                    onChange={onInputChange}
                    error={stateProvinceError}
                ></TextField>
            </GridItem>
            <GridItem {...styles.zipPostcodeGridItem}>
                <TextField
                    label={translate("Zip/Postcode")}
                    value={zipPostcode || ""}
                    name="zipPostcode"
                    required={true}
                    onChange={onInputChange}
                    error={zipPostcodeError}
                ></TextField>
            </GridItem>
            <GridItem {...styles.countryGridItem}>
                <Select
                    label={translate("Country")}
                    value={country || ""}
                    name="country"
                    onChange={onSelectCountryChange}
                    error={countryError}
                >
                    <option value="" disabled={true}>
                        {translate("Select One")}
                    </option>
                    {allCountries?.map(option => (
                        <option key={option.id} value={option.name}>
                            {option.name}
                        </option>
                    ))}
                </Select>
            </GridItem>
            <GridItem {...styles.phoneNumberGridItem}>
                <TextField
                    label={translate("Phone Number")}
                    value={phoneNumber || ""}
                    name="phoneNumber"
                    required={true}
                    onChange={onInputChange}
                    error={phoneNumberError}
                ></TextField>
            </GridItem>
            <GridItem {...styles.faxNumberGridItem}>
                <TextField
                    label={translate("Fax Number")}
                    value={faxNumber || ""}
                    name="faxNumber"
                    onChange={onInputChange}
                ></TextField>
            </GridItem>
            <GridItem {...styles.companyEmailGridItem}>
                <TextField
                    label={translate("Company Email")}
                    value={companyEmail || ""}
                    name="companyEmail"
                    required={true}
                    onChange={onInputChange}
                    error={companyEmailError}
                ></TextField>
            </GridItem>
            <GridItem {...styles.companyWebsiteGridItem}>
                <TextField
                    label={translate("Company Website")}
                    value={companyWebsite || ""}
                    name="companyWebsite"
                    onChange={onInputChange}
                ></TextField>
            </GridItem>
            <GridItem {...styles.businessTypeGridItem}>
                <Select
                    label={translate("Business Type")}
                    value={businessType || ""}
                    name="businessType"
                    onChange={onSelectChange}
                >
                    <option value="" disabled={true}>
                        {translate("Select One")}
                    </option>
                    {fields.businessTypeOptions?.map(option => (
                        <option key={option.fields?.value} value={translate(option.fields?.value)}>
                            {translate(option.fields?.value)}
                        </option>
                    ))}
                </Select>
            </GridItem>
            <GridItem {...styles.businessDescriptionGridItem}>
                <TextArea
                    label={translate("Business Description")}
                    value={businessDescription || ""}
                    name="businessDescription"
                    onChange={onTextAreaChange}
                ></TextArea>
            </GridItem>
            <GridItem {...styles.vatNumberGridItem}>
                <TextField
                    label={translate("VAT/ABN Number")}
                    value={vatNumber || ""}
                    name="vatNumber"
                    onChange={onInputChange}
                    required={vatNumberIsRequired}
                    error={vatNumberError}
                ></TextField>
            </GridItem>
            <GridItem {...styles.nzCustomsNumberGridItem}>
                <TextField
                    label={translate("New Zealand Customs Client Number (NZ only)")}
                    value={nzCustomsNumber || ""}
                    name="nzCustomsNumber"
                    onChange={onInputChange}
                ></TextField>
            </GridItem>
            <GridItem {...styles.submitButtonGridItem}>
                <StyledWrapper {...styles.submitButtonWrapper}>
                    {formErrorMessage && <Typography {...styles.errorMessage}>{formErrorMessage}</Typography>}
                    <Button onClick={submitForm}>{translate("Submit Application")}</Button>
                </StyledWrapper>
            </GridItem>
            <Modal
                isOpen={successModalIsOpen}
                closeOnEsc={true}
                handleClose={closeSuccessModal}
                headline={fields.successMessageTitle}
            >
                {fields.successMessage}
            </Modal>
            <Modal
                isOpen={errorModalIsOpen}
                closeOnEsc={true}
                handleClose={closeErrorModal}
                headline={fields.errorMessageTitle}
            >
                {fields.errorMessage}
            </Modal>
        </GridContainer>
    );
};

const businessTypeOptionsFieldDefinition: ListFieldDefinition = {
    name: fields.businessTypeOptions,
    editorTemplate: "ListField",
    getDisplay: (item: HasFields, state) => {
        return item.fields.value;
    },
    defaultValue: [
        {
            fields: {
                value: "Retail Store with Website",
            },
        },
        {
            fields: {
                value: "Boatbuilder",
            },
        },
        {
            fields: {
                value: "Installer",
            },
        },
        {
            fields: {
                value: "Retail Store",
            },
        },
        {
            fields: {
                value: "Online Retailer",
            },
        },
        {
            fields: {
                value: "Boat Yard",
            },
        },
        {
            fields: {
                value: "Marina",
            },
        },
        {
            fields: {
                value: "Fleet Manager",
            },
        },
        {
            fields: {
                value: "Other",
            },
        },
    ],
    fieldType: "General",
    fieldDefinitions: [
        {
            name: "value",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(DealerAccoutnRequestForm)),
    definition: {
        group: "Common",
        fieldDefinitions: [
            businessTypeOptionsFieldDefinition,
            {
                name: fields.successMessageTitle,
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "Request Recieved!",
            },
            {
                name: fields.successMessage,
                editorTemplate: "RichTextField",
                fieldType: "General",
                defaultValue: "We've received your request. One of our representatives will be in contact soon!",
            },
            {
                name: fields.errorMessageTitle,
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "An Error Occurred",
            },
            {
                name: fields.errorMessage,
                editorTemplate: "RichTextField",
                fieldType: "General",
                defaultValue:
                    "An error occurred so we didn't receive your message. Please try again or use the Contact Us form instead.",
            },
        ],
    },
};

export default widgetModule;
